import React, { useEffect, useState } from 'react';
import dayAwayService, { TimeOffEntry, DashboardData } from '../../services/dayAwayService';
import { teamService, Team } from '../../services/teamService';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Calendar as CalendarIcon, Users, Clock, RefreshCw } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventContentArg } from '@fullcalendar/core';
import { format, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";

const motivationalMessages = [
  "Rest is not a reward, it's a necessity for peak performance and creativity! ",
  "Taking time to recharge isn't just good for you—it's great for the whole team! ",
  "Balance in work and life leads to better outcomes for everyone! ",
  "Quality rest today leads to quality work tomorrow! ",
  "A well-rested team is a creative and productive team! ",
];

const MotivationalMessage: React.FC = () => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % motivationalMessages.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-2 rounded-lg mb-3 text-center">
      <p className="text-cyan-700 dark:text-cyan-300 font-medium">{motivationalMessages[messageIndex]}</p>
    </div>
  );
};

const Calendar: React.FC = () => {
  const { userEmail } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<TimeOffEntry[]>([]);

  // Person Viewer state
  const [employees, setEmployees] = useState<string[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [personEntries, setPersonEntries] = useState<TimeOffEntry[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Loading state for initial data load
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState<string>('Initializing...');
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Tooltip state
  const [tooltipData, setTooltipData] = useState<{ employees: TimeOffEntry[]; x: number; y: number } | null>(null);

  // Team state
  const [team, setTeam] = useState<Team | null>(null);

  // Load team data
  useEffect(() => {
    const loadTeam = async () => {
      if (!userEmail) return;
      try {
        const teamData = await teamService.getTeam(userEmail);
        setTeam(teamData);
      } catch (error) {
        console.error('Error loading team:', error);
      }
    };
    loadTeam();
  }, [userEmail]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeeList = await dayAwayService.getEmployeeNames();
        setEmployees(employeeList);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchTimeOff = async () => {
      if (!selectedEmployee) return;

      try {
        // NEW: Use optimized approach - get current 3-month data first
        let allEntries = await dayAwayService.getCalendarThreeMonthData();
        
        // If no entries found in 3-month data, try getting more historical data
        const employeeEntries = allEntries.filter(
          entry => entry.employee === selectedEmployee
        );
        
        // If we need more historical data for this employee, fetch it
        if (employeeEntries.length === 0) {
          // Only fetch full data if really needed for this specific employee
          console.log(`No entries found for ${selectedEmployee} in 3-month data, checking historical data...`);
          const historicalEntries = await dayAwayService.getHistoricalCache();
          const historicalEmployeeEntries = historicalEntries.filter(
            entry => entry.employee === selectedEmployee
          );
          setPersonEntries(historicalEmployeeEntries);
        } else {
          setPersonEntries(employeeEntries);
        }
      } catch (error) {
        console.error('Error fetching time off entries:', error);
      }
    };

    fetchTimeOff();
  }, [selectedEmployee]);

  // NEW: Optimized data loading for calendar
  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        console.log('Calendar: Starting optimized calendar loading...');
        setIsInitialLoading(true);
        setLoadingStep('Loading calendar data...');
        setLoadingProgress(20);
        
        // Use the new optimized calendar dashboard function
        const data = await dayAwayService.getCalendarDashboardData();
        setLoadingProgress(80);
        
        console.log('Calendar: Optimized loading completed, got data:', {
          upcomingTimeOff: data.upcomingTimeOff.length,
          calendarData: data.calendarData.length
        });
        
        setDashboardData(data);
        setLoadingProgress(100);
        
      } catch (error) {
        console.error('Error in optimized calendar loading:', error);
        setLoadingStep('Error loading calendar data');
        setLoadingProgress(100);
      } finally {
        // Small delay to show completion
        setTimeout(() => {
          setIsInitialLoading(false);
          setIsSyncing(false);
        }, 300);
      }
    };

    fetchCalendarData();
  }, []);

  // Track which months we've already loaded to prevent infinite loops
  const [loadedMonths, setLoadedMonths] = React.useState<Set<string>>(new Set());
  
  // Navigation loading state
  const [isNavigationLoading, setIsNavigationLoading] = useState(false);
  const [navigationLoadingStep, setNavigationLoadingStep] = useState<string>('');

  // NEW: Handle calendar navigation (month changes) - with proper month detection
  const handleCalendarNavigation = React.useCallback(async (dateInfo: any) => {
    // Get the actual calendar view date (middle of the month range)
    const viewStart = new Date(dateInfo.start);
    const viewEnd = new Date(dateInfo.end);
    
    // Calculate the middle date to get the actual month being viewed
    const middleDate = new Date(viewStart.getTime() + (viewEnd.getTime() - viewStart.getTime()) / 2);
    const targetMonth = middleDate.getMonth();
    const targetYear = middleDate.getFullYear();
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    // Create a unique key for this month
    const monthKey = `${targetYear}-${targetMonth}`;
    
    // Check if we've already loaded this month
    if (loadedMonths.has(monthKey)) {
      return;
    }
    
    // Check if we're navigating to a month outside the initial 3-month range
    const monthDiff = (targetYear - currentYear) * 12 + (targetMonth - currentMonth);
    const isOutsideInitialRange = monthDiff < 0 || monthDiff > 2;
    
    if (isOutsideInitialRange) {
      const monthName = new Date(targetYear, targetMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      console.log(`Navigating to month outside initial range: ${monthName}`);
      
      // Show loading modal
      setIsNavigationLoading(true);
      setNavigationLoadingStep(`Loading data for ${monthName}...`);
      
      // Mark this month as being loaded
      setLoadedMonths(prev => new Set([...prev, monthKey]));
      
      try {
        // Fetch data for the specific month
        const monthData = await dayAwayService.getCalendarMonthData(targetYear, targetMonth);
        
        // Update calendar data to include this month's data
        if (monthData.length > 0) {
          const monthCalendarData = monthData.reduce((acc: { [key: string]: TimeOffEntry[] }, entry) => {
            if (!acc[entry.date]) {
              acc[entry.date] = [];
            }
            acc[entry.date].push(entry);
            return acc;
          }, {});
          
          // Merge with existing calendar data
          setDashboardData(prev => {
            if (!prev) return {
              upcomingTimeOff: [],
              calendarData: Object.entries(monthCalendarData).map(([date, employees]) => ({ date, employees }))
            };
            
            const updatedCalendarData = [...prev.calendarData];
            Object.entries(monthCalendarData).forEach(([date, employees]) => {
              const existingIndex = updatedCalendarData.findIndex(item => item.date === date);
              if (existingIndex >= 0) {
                updatedCalendarData[existingIndex] = { date, employees };
              } else {
                updatedCalendarData.push({ date, employees });
              }
            });
            
            return {
              ...prev,
              calendarData: updatedCalendarData
            };
          });
        }
      } catch (error) {
        console.error('Error loading month data:', error);
        // Remove from loaded months if there was an error
        setLoadedMonths(prev => {
          const newSet = new Set(prev);
          newSet.delete(monthKey);
          return newSet;
        });
      } finally {
        // Hide loading modal
        setTimeout(() => {
          setIsNavigationLoading(false);
          setNavigationLoadingStep('');
        }, 300);
      }
    }
  }, [loadedMonths]);

  // NEW: Handle manual sync button
  const handleManualSync = async () => {
    setIsSyncing(true);
    try {
      const success = await dayAwayService.syncCalendarData();
      if (success) {
        // Refresh the dashboard data
        const freshData = await dayAwayService.getCalendarDashboardData();
        setDashboardData(freshData);
      }
    } catch (error) {
      console.error('Error in manual sync:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDateClick = (arg: { dateStr: string }) => {
    const dateData = dashboardData?.calendarData.find((d: { date: string; employees: TimeOffEntry[] }) => d.date === arg.dateStr);
    
    if (dateData && dateData.employees.length > 0) {
      setSelectedDate(arg.dateStr);
      setSelectedEmployees(dateData.employees);
      setDialogOpen(true);
    }
  };


  const getCalendarEvents = () => {
    if (!dashboardData?.calendarData) return [];

    return dashboardData.calendarData.map((data: { date: string; employees: TimeOffEntry[] }) => ({
      title: `${data.employees.length} off`,
      date: data.date,
      backgroundColor: data.employees.length >= 10 ? 'rgba(239, 68, 68, 0.4)' : // red for 10+
                      data.employees.length >= 5 ? 'rgba(245, 158, 11, 0.4)' : // orange for 5-9
                      'rgba(59, 130, 246, 0.4)', // blue for 1-4
      borderColor: 'transparent',
      display: 'background',
      employees: data.employees,
      classNames: ['cursor-pointer']
    }));
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    const count = eventInfo.event.extendedProps.employees?.length || 0;
    if (count === 0) return null;

    const textColor = count >= 10 ? 'text-red-700 dark:text-red-200' :
                     count >= 5 ? 'text-amber-700 dark:text-amber-200' :
                     'text-blue-700 dark:text-blue-200';

    return (
      <div className={`absolute inset-0 flex flex-col items-center justify-center text-center`}>
        <div className={`text-lg font-bold ${textColor}`}>{count}</div>
        <div className={`text-xs ${textColor} hidden sm:block`}>
          {count === 1 ? 'person' : 'people'}
        </div>
        <div className={`text-xs ${textColor} hidden sm:block`}>off</div>
      </div>
    );
  };

  const getTimeOffBadgeColor = (type: TimeOffEntry['timeOffType']) => {
    switch (type) {
      case 'Full':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'Half':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'Email Only':
        return 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
    }
  };

  const pastEntries = personEntries.filter(
    entry => new Date(entry.date) <= new Date()
  ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const futureEntries = personEntries.filter(
    entry => new Date(entry.date) > new Date()
  ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Helper function to group time off entries by person
  const groupEntriesByPerson = (entries: TimeOffEntry[]) => {
    const grouped: { [person: string]: TimeOffEntry[] } = {};
    entries.forEach(entry => {
      if (!grouped[entry.employee]) {
        grouped[entry.employee] = [];
      }
      grouped[entry.employee].push(entry);
    });
    
    // Sort entries for each person by date
    Object.keys(grouped).forEach(person => {
      grouped[person].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });
    
    return grouped;
  };


  // Group upcoming time off by person for Next 7 Days
  const groupedUpcomingTimeOff = groupEntriesByPerson(dashboardData?.upcomingTimeOff || []);

  // State for team-specific time off data
  const [teamTimeOffData, setTeamTimeOffData] = useState<TimeOffEntry[]>([]);

  // Load team-specific time off data when team changes
  useEffect(() => {
    const loadTeamTimeOffData = async () => {
      if (!team?.members.length) {
        setTeamTimeOffData([]);
        return;
      }

      try {
        const teamMemberNames = team.members.map(member => member.name);
        const allTeamData = await dayAwayService.getTeamTimeOffData(teamMemberNames);
        
        // Filter for upcoming time off (next 30 days to capture more team data)
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);
        
        const upcomingTeamTimeOff = allTeamData.filter(entry => {
          try {
            const entryDate = parseISO(entry.date);
            return entryDate >= now && entryDate <= thirtyDaysFromNow;
          } catch (err) {
            return false;
          }
        });
        
        setTeamTimeOffData(upcomingTeamTimeOff);
      } catch (error) {
        setTeamTimeOffData([]);
      }
    };

    loadTeamTimeOffData();
  }, [team]);

  // Group team time off by person for Upcoming Team Time Off
  const groupedTeamTimeOff = groupEntriesByPerson(teamTimeOffData);

  return (
    <div className="p-4" onMouseEnter={() => setTooltipData(null)}>
      <div className="flex-1 space-y-2 p-4 pt-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Calendar</h2>
          <p className="text-gray-500 dark:text-gray-400">
            View and monitor employee time off calendar
          </p>
        </div>
      </div>

      <MotivationalMessage />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Upcoming Team Time Off */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                Upcoming Team Time Off
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(groupedTeamTimeOff).length > 0 ? (
                <ScrollArea className={`${Object.keys(groupedTeamTimeOff).length > 3 ? 'h-[300px]' : 'h-auto max-h-[300px]'} pr-4`}>
                  <div className="space-y-3">
                    {Object.entries(groupedTeamTimeOff).map(([person, entries]) => (
                      <div
                        key={person}
                        className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="text-base font-medium text-gray-900 dark:text-white mb-2">
                          {person}
                        </div>
                        <div className="space-y-1">
                          {entries.map((entry, index) => {
                            try {
                              const date = parseISO(entry.date);
                              const noteText = entry.note ? ` - ${entry.note}` : '';
                              return (
                                <div
                                  key={entry.id || `${entry.date}-${entry.employee}-${index}`}
                                  className="text-sm text-gray-600 dark:text-gray-400"
                                >
                                  {format(date, 'MMMM d, yyyy')} - {entry.timeOffType}{noteText}
                                </div>
                              );
                            } catch (error) {
                              console.error('Error rendering time off entry:', error);
                              return null;
                            }
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  {!team?.members.length
                    ? "No team members added yet. Add team members in the My Team page."
                    : teamTimeOffData.length === 0
                      ? "Loading team time off data..."
                      : "No upcoming team time off"
                  }
                </div>
              )}
            </CardContent>
          </Card>

          {/* Calendar Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                Time Off Calendar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ 
                '--fc-daygrid-event-dot-width': '0px',
                '--fc-day-grid-event-padding-vertical': '2px',
                '--fc-daygrid-event-margin-vertical': '2px',
                '--fc-border-color': 'rgb(51, 65, 85)',
                '--fc-page-bg-color': 'transparent',
                '--fc-neutral-bg-color': 'transparent',
                '--fc-today-bg-color': 'rgb(15, 23, 42)',
                '--fc-button-bg-color': 'rgb(51, 65, 85)',
                '--fc-button-border-color': 'rgb(51, 65, 85)',
                '--fc-button-hover-bg-color': 'rgb(71, 85, 105)',
                '--fc-button-hover-border-color': 'rgb(71, 85, 105)',
                '--fc-button-active-bg-color': 'rgb(71, 85, 105)',
                '--fc-button-active-border-color': 'rgb(71, 85, 105)',
                '--fc-event-bg-color': 'transparent',
                '--fc-event-border-color': 'transparent',
                '--fc-neutral-text-color': 'rgb(229, 231, 235)',
                '--fc-button-text-color': 'rgb(229, 231, 235)',
                '--fc-daygrid-day-frame-height': '80px'
              } as React.CSSProperties}
              className="dark:[&_.fc-button]:!text-gray-200 dark:[&_.fc-toolbar-title]:!text-white dark:[&_.fc-col-header-cell]:!text-gray-200 dark:[&_.fc-daygrid-day-number]:!text-gray-200 [&_.fc-daygrid-day]:hover:bg-gray-50 dark:[&_.fc-daygrid-day]:hover:bg-gray-600/30">
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  dateClick={handleDateClick}
                  events={getCalendarEvents()}
                  eventContent={renderEventContent}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'syncButton'
                  }}
                  customButtons={{
                    syncButton: {
                      text: isSyncing ? 'Syncing...' : 'Sync',
                      click: handleManualSync
                    }
                  }}
                  validRange={{
                    start: '2024-01-01',
                    end: '2025-12-31'
                  }}
                  dayMaxEvents={true}
                  contentHeight="auto"
                  dayCellClassNames="!py-0.5"
                  fixedWeekCount={false}
                  datesSet={handleCalendarNavigation}
                  eventMouseEnter={(info) => {
                    const dateStr = info.event.startStr;
                    const dateData = dashboardData?.calendarData.find((d: { date: string; employees: TimeOffEntry[] }) => d.date === dateStr);
                    
                    if (dateData && dateData.employees.length > 0) {
                      const rect = info.el.getBoundingClientRect();
                      setTooltipData({
                        employees: dateData.employees,
                        x: rect.left + rect.width / 2,
                        y: rect.top - 10
                      });
                    }
                  }}
                  eventMouseLeave={() => {
                    // Use a longer delay and check if mouse is actually outside
                    setTimeout(() => {
                      // Only hide if mouse is not over the tooltip area
                      const mouseEvent = new MouseEvent('mousemove');
                      if (!document.elementFromPoint(mouseEvent.clientX || 0, mouseEvent.clientY || 0)?.closest('.tooltip-container')) {
                        setTooltipData(null);
                      }
                    }, 200);
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Person Viewer Card */}
          <Card onMouseEnter={() => setTooltipData(null)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                  Person Viewer
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={selectedEmployee || ''}
                onValueChange={setSelectedEmployee}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    .filter(employee => employee && employee.trim() !== '')
                    .map((employee) => (
                      <SelectItem key={`employee-${employee}`} value={employee}>
                        {employee}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              {selectedEmployee && (
                <div className="mt-4 space-y-4">
                  {/* Future Time Off */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                        Upcoming Time Off
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {futureEntries.length} entries
                      </span>
                    </div>
                    <ScrollArea className="h-[250px] pr-4">
                      <div className="space-y-2">
                        {futureEntries.length > 0 ? (
                          futureEntries.map((entry, index) => (
                            <div
                              key={entry.id || `${entry.date}-${entry.employee}-${index}`}
                              className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {format(parseISO(entry.date), 'MM/dd/yyyy')}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{entry.timeOffType}</p>
                              </div>
                              {entry.note && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{entry.note}</div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400">
                            No upcoming time off
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* Past Time Off */}
                  <div className="border-t-2 border-gray-300 dark:border-gray-600 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                        Past Time Off
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {pastEntries.length} entries
                      </span>
                    </div>
                    <ScrollArea className="h-[250px] pr-4">
                      <div className="space-y-2">
                        {pastEntries.length > 0 ? (
                          pastEntries.map((entry, index) => (
                            <div
                              key={entry.id || `${entry.date}-${entry.employee}-${index}`}
                              className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {format(parseISO(entry.date), 'MM/dd/yyyy')}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{entry.timeOffType}</p>
                              </div>
                              {entry.note && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{entry.note}</div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400">
                            No past time off
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Next 7 Days */}
        <Card
          onMouseEnter={() => setTooltipData(null)}
          className="flex flex-col h-fit"
        >
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              Next 7 Days
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 h-[800px] overflow-y-auto">
            {Object.keys(groupedUpcomingTimeOff).length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No upcoming time off in the next 7 days
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupedUpcomingTimeOff).map(([person, entries]) => (
                  <div
                    key={person}
                    className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="text-base font-medium text-gray-900 dark:text-white mb-2">
                      {person}
                    </div>
                    <div className="space-y-1">
                      {entries.map((entry, index) => {
                        try {
                          const date = parseISO(entry.date);
                          const noteText = entry.note ? ` - ${entry.note}` : '';
                          return (
                            <div
                              key={entry.id || `${entry.date}-${entry.employee}-${index}`}
                              className="text-sm text-gray-600 dark:text-gray-400"
                            >
                              {format(date, 'MMMM d, yyyy')} - {entry.timeOffType}{noteText}
                            </div>
                          );
                        } catch (error) {
                          return null;
                        }
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl w-[90vw] bg-white dark:bg-gray-800 border dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">
              {selectedDate && `Employees Off - ${format(parseISO(selectedDate), 'MMMM d, yyyy')}`}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[calc(90vh-8rem)] pr-2">
            <div className="space-y-2">
              {selectedEmployees.map((employee, index) => (
                <div
                  key={employee.id || `${employee.date}-${employee.employee}-${index}`}
                  className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/70 whitespace-nowrap">
                    {employee.employee}
                  </Badge>
                  <Badge variant="outline" className={`${getTimeOffBadgeColor(employee.timeOffType)} whitespace-nowrap`}>
                    {employee.timeOffType}
                  </Badge>
                  {employee.note && (
                    <span className="text-sm text-gray-600 dark:text-gray-300 flex-1 break-words">{employee.note}</span>
                  )}
                </div>
              ))}
              {selectedEmployees.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No time off entries for this date
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Tooltip */}
      {tooltipData && (
        <div
          className="tooltip-container fixed z-50 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3 max-w-xs"
          style={{
            left: `${tooltipData.x}px`,
            top: `${tooltipData.y}px`,
            transform: 'translate(-50%, -100%)',
          }}
          onMouseEnter={() => {
            // Keep tooltip open when hovering over it
          }}
          onMouseLeave={() => {
            setTooltipData(null);
          }}
        >
          <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            People off today:
          </div>
          <div className="space-y-1">
            {tooltipData.employees.slice(0, 5).map((employee, index) => (
              <div key={index} className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="text-xs">
                  {employee.employee}
                </Badge>
                <span className="text-gray-600 dark:text-gray-400">
                  {employee.timeOffType}
                </span>
              </div>
            ))}
            {tooltipData.employees.length > 5 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                +{tooltipData.employees.length - 5} more...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {isInitialLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              {/* Loading Icon */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Loading Calendar Data
              </h3>
              
              {/* Loading Step */}
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {loadingStep}
              </p>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                <div
                  className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
              
              {/* Progress Percentage */}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {loadingProgress}% Complete
              </p>
              
              {/* Additional Info */}
              <div className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                <p>Using optimized loading for faster performance</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Loading Modal */}
      {isNavigationLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              {/* Loading Icon */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Loading Month Data
              </h3>
              
              {/* Loading Step */}
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {navigationLoadingStep}
              </p>
              
              {/* Additional Info */}
              <div className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                <p>Fetching calendar data for the selected month</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;